/**
 * Reason codes (spec §6.3) for badges and panels. The codes and the Persian «why» and
 * «next step» texts come from `@mfa-aff/shared` (reasons.ts, the same copy the API returns);
 * this file only adds the short badge label, tone and whether a claim makes sense.
 * The other partner's name is never shown.
 */
import { REASONS_FA, REASON_CODES, type ReasonCode, isReasonCode, reasonNextStep, reasonText } from "@mfa-aff/shared";

export { REASON_CODES, isReasonCode, reasonNextStep, reasonText };
export type { ReasonCode };

export type ReasonTone = "gray" | "warning" | "error" | "brand" | "success";

export type ReasonCopy = {
    /** Short badge label. */
    label: string;
    /** One sentence: why (shared copy). */
    why: string;
    /** One sentence: what to do next (shared copy). */
    next: string;
    tone: ReasonTone;
    /** Whether a claim (ادعا) makes sense for this reason. */
    claimable: boolean;
};

const META: Record<ReasonCode, { label: string; tone: ReasonTone; claimable: boolean }> = {
    DOMAIN_ONLY: { label: "فقط دامنه", tone: "gray", claimable: false },
    REFUNDED_IN_GUARANTEE: { label: "بازگشت وجه", tone: "error", claimable: false },
    SELF_REFERRAL: { label: "خرید برای خودتان", tone: "error", claimable: true },
    ATTRIBUTED_TO_OTHER_CODE: { label: "کد دیگری استفاده شد", tone: "warning", claimable: true },
    BOUND_TO_OTHER_AFFILIATE: { label: "مشتری بازاریاب دیگر", tone: "warning", claimable: true },
    NO_SIGNAL: { label: "بدون کد یا لینک", tone: "gray", claimable: true },
    CODE_LIMIT_REACHED: { label: "سقف کد پر شده", tone: "warning", claimable: false },
    SERVICE_NOT_ACTIVE: { label: "سرویس فعال نیست", tone: "error", claimable: false },
    UNDER_REVIEW: { label: "در حال بررسی", tone: "brand", claimable: false },
    NOT_ELIGIBLE_PRODUCT: { label: "محصول بدون پورسانت", tone: "gray", claimable: false },
    REFUNDED_AFTER_APPROVAL: { label: "بازپس‌گیری متناسب", tone: "warning", claimable: false },
    FRAUD: { label: "نامعتبر در بررسی امنیتی", tone: "error", claimable: false },
    MANUAL_ASSIGNMENT: { label: "ثبت دستی", tone: "success", claimable: false },
    PRE_CUTOVER: { label: "پیش از انتقال", tone: "gray", claimable: false },
    CANCELLED_EARLY: { label: "لغو زودهنگام", tone: "warning", claimable: false },
    AFFILIATE_CLOSED: { label: "حساب بسته شده", tone: "error", claimable: false },
    CAMPAIGN_DISCOUNT: { label: "تخفیف بیش از سقف در کمپین", tone: "warning", claimable: false },
    AFFILIATE_SUSPENDED: { label: "حساب معلق", tone: "warning", claimable: false },
};

export const REASONS: Record<ReasonCode, ReasonCopy> = Object.fromEntries(
    REASON_CODES.map((code) => [code, { ...META[code], why: REASONS_FA[code].text, next: REASONS_FA[code].nextStep }]),
) as Record<ReasonCode, ReasonCopy>;
